import axios, { AxiosProgressEvent } from 'axios';
import ls from 'localstorage-slim';
import { AuthState } from '../redux/auth/types';
import type {
  APIVersions as APIVersionsType,
  CarData,
  FeedbackAnswer,
  FeedbackBrowserData,
  UserExperience,
} from '../types/instant-lease-api';
import {
  APIPaths,
  APIVersions,
  Contracts,
  CrefoCompany,
  Dealer,
  LeaseApplication,
  MarketingData,
  Partner,
  Type,
} from '../types/instant-lease-api';
import SanitizeDataValue, {
  formatLeaseApplication,
} from '../utils/sanitize-data';
import sandboxFunction from './sandbox-api';
import { contract, contracts } from './sandbox-contracts-api';

export interface BankDetails {
  valid: boolean;
  messages: string[];
  iban: string;
  bankData: {
    bankCode: string;
    name: string;
    zip: string;
    city: string;
    bic: string;
  };
  checkResults: {
    bankCode: boolean;
  };
}

const REQUEST_TIMEOUT = 20_000;

const sandboxIsActive = ls.get('sandbox', { decrypt: true }) === 'true';

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  scope: string;
  token_type: string;
  expires_in: number;
}

export const backendLogin = async (
  email: string,
  password: string,
): Promise<LoginResponse> => {
  try {
    const response = await axios.post<LoginResponse>(
      '/api/lease-application/login',
      {
        username: email,
        password,
      },
    );
    return response.data;
  } catch (error: any) {
    console.error('Login error details:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      headers: error.response?.headers,
      config: error.config,
    });
    throw error;
  }
};

export interface RegisterResponse {
  status: string;
  message: string;
  timestamp: string;
  signed_up_user: {
    id: string;
    username: string;
    offerId: string;
    partner: string;
    createdAt: string;
  };
}

export const activateUser = async (
  activationToken: string,
): Promise<RegisterResponse> => {
  try {
    const response = await axios.patch<RegisterResponse>(
      `/api/lease-application/v2/activate/${activationToken}`,
    );
    return response.data;
  } catch (error: any) {
    console.error('Activation error details:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      headers: error.response?.headers,
      config: error.config,
    });
    throw error;
  }
};

export const fetchLeaseApplications = (
  accessToken: string,
): Promise<LeaseApplication[]> => {
  return sandboxIsActive
    ? ([sandboxFunction({ type: 'get' })] as any)
    : new Promise<LeaseApplication[]>((resolve, reject) => {
        axios
          .get<{ lease_applications: LeaseApplication[] }>(
            '/api/lease-application',
            {
              params: {
                active_only: true,
              },
              timeout: REQUEST_TIMEOUT,
              method: 'get',
              headers: {
                'Accept-Language': 'de-DE',
                Accept: 'application/json',
                Authorization: `Bearer ${accessToken}`,
              },
            },
          )
          .then((response) => resolve(response?.data?.lease_applications))
          .catch((error) =>
            reject({ error: error, message: error?.response?.data }),
          );
      });
};

export const fetchLeaseApplication = (
  accessToken: string,
  leaseAppId: string,
  silent?: boolean,
): Promise<LeaseApplication> => {
  return sandboxIsActive
    ? new Promise<LeaseApplication>((resolve) => {
        resolve(sandboxFunction({ type: 'get' }));
      })
    : new Promise<LeaseApplication>((resolve, reject) => {
        const headers: Record<string, string> = {
          'Accept-Language': 'de-DE',
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        };
        if (silent) {
          headers['X-Silent'] = 'true';
        }

        axios
          .get<LeaseApplication>(`/api/lease-application/${leaseAppId}`, {
            timeout: REQUEST_TIMEOUT,
            headers,
          })
          .then((response) => resolve(formatLeaseApplication(response?.data)))
          .catch((error) =>
            reject({ error: error, message: error?.response?.data }),
          );
      });
};

export const fetchCompanyDetails = (
  accessToken: string,
  companyName: string,
  zipCode: string,
): Promise<CrefoCompany[]> => {
  return new Promise((resolve, reject) => {
    axios
      .get(`/api/crefo/search/${companyName}%20${zipCode}`, {
        timeout: REQUEST_TIMEOUT,
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .then(({ data }) => resolve(data?.companyNameSuggestions))
      .catch((error) =>
        reject({ error: error, message: error?.response?.data }),
      );
  });
};

export const fetchDealers = (
  dealerList: string,
  partner: string,
): Promise<Dealer[]> => {
  return new Promise<Dealer[]>((resolve, reject) => {
    axios
      .get<Dealer[]>(`/dealerlist`, {
        params: {
          partner: partner.toLocaleLowerCase(),
          brand: dealerList.toLowerCase(),
        },
        timeout: REQUEST_TIMEOUT,
      })
      .then(({ data }) => resolve(data))
      .catch((error) =>
        reject({ error: error, message: error?.response?.data }),
      );
  });
};

export const fetchDealersLegacy = (
  dealerList: string,
  partner: string,
): Promise<Dealer[]> => {
  return new Promise<Dealer[]>((resolve, reject) => {
    axios
      .get<Dealer[]>(`/api/dealer-search/v1/dealers`, {
        params: {
          partner: partner.toLocaleLowerCase(),
          brand: dealerList.toLowerCase(),
        },
        timeout: REQUEST_TIMEOUT,
        headers: {
          Accept: 'application/json',
          'X-Silent': 'true',
        },
      })
      .then(({ data }) => resolve(data))
      .catch((error) =>
        reject({ error: error, message: error?.response?.data }),
      );
  });
};

const fetchCarData = (path: string): Promise<CarData> =>
  new Promise((resolve, reject) => {
    axios
      .get<CarData>(`/v1/Campaigns/CarData/${path}`, {
        timeout: REQUEST_TIMEOUT,
        headers: {
          Accept: 'application/json',
        },
      })
      .then(({ data }) => resolve(data))
      .catch((error) => reject({ error: error }));
  });

export const fetchCarDetails = ({
  partner,
  publicationId,
  pricingId,
  offerId,
}: {
  partner?: Partner | undefined | null;
  publicationId?: string | undefined | null;
  pricingId?: string | undefined | null;
  offerId?: string | undefined | null;
}): Promise<CarData> => {
  if (partner === Partner.ALD_FLEX && offerId) {
    return fetchCarData(`flex/${offerId}`);
  } else if (partner === Partner.KIA_FLEX && publicationId && pricingId) {
    return fetchCarData(`DUFLEX/${publicationId}/${pricingId}`);
  } else if (publicationId && pricingId) {
    return fetchCarData(`${publicationId}/${pricingId}`);
  } else if (offerId) {
    return fetchCarData(`flex/${offerId}`);
  } else {
    return Promise.reject(
      new Error('At least publicationId and pricingId or offerId are required'),
    );
  }
};

export const fetchBankDetails = (iban: string): Promise<BankDetails> => {
  return new Promise<BankDetails>((resolve, reject) => {
    axios
      .get<BankDetails>(
        `/api/iban/validate/${iban}?validateBankCode=true&getBIC=true`,
        {
          timeout: REQUEST_TIMEOUT,
          headers: {
            Accept: 'application/json',
          },
        },
      )
      .then((response) => resolve(response?.data))
      .catch((error) =>
        reject({ error: error, message: error?.response?.data }),
      );
  });
};

export const getSavedUserOffer = async ({
  accessToken,
  email,
  offerId,
}: {
  accessToken: string;
  email: string;
  offerId?: string | null;
}): Promise<any> => {
  return new Promise<LeaseApplication>((resolve, reject) => {
    axios
      .get<LeaseApplication>(
        `/api/lease-application/userdetails/${email}${
          offerId ? `/${offerId}` : ''
        }`,
        {
          timeout: REQUEST_TIMEOUT,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )
      .then((response: any) => resolve(response))
      .catch((error) =>
        reject({ error: error, message: error?.response?.data }),
      );
  });
};

export const saveUserOffer = async ({
  accessToken,
  email,
  offerId,
  vin,
  pricingId,
  publicationId,
  partner,
  loyaltyId,
  loyaltyType,
  offerRefId,
  type,
}: {
  accessToken: string;
  email: string;
  offerId?: string;
  vin?: string;
  pricingId?: string;
  publicationId?: string;
  partner: string;
  loyaltyId?: string;
  loyaltyType?: string;
  offerRefId?: string;
  type?: string;
}): Promise<any> => {
  return new Promise<LeaseApplication>((resolve, reject) => {
    axios
      .post<LeaseApplication>(
        `/api/lease-application/userdetails`,
        {
          email,
          offerId,
          vin,
          pricingId,
          publicationId,
          partner: partner.toUpperCase(),
          loyaltyId,
          loyaltyType,
          offerRefId,
          type,
        },
        {
          timeout: REQUEST_TIMEOUT,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )
      .then((response: any) => resolve(response))
      .catch((error) =>
        reject({ error: error, message: error?.response?.data }),
      );
  });
};

export const deleteSavedUserOffer = async ({
  accessToken,
  email,
  offerId,
}: {
  accessToken: string;
  email: string;
  offerId: string | null;
}): Promise<any> => {
  return new Promise<LeaseApplication>((resolve, reject) => {
    axios
      .delete<LeaseApplication>(
        `/api/lease-application/userdetails/${offerId}-${email}`,
        {
          timeout: REQUEST_TIMEOUT,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )
      .then((response: any) => resolve(response))
      .catch((error) =>
        reject({ error: error, message: error?.response?.data }),
      );
  });
};

export const createLeaseApplication = ({
  offerId,
  partner,
  type,
  pricingId,
  publicationId,
  vin,
  accessToken,
  loyaltyId,
  loyaltyType,
  marketingData,
  offerRefId,
}: {
  offerId?: string;
  partner?: Partner;
  type?: Type;
  pricingId?: string;
  publicationId?: string;
  vin?: string;
  accessToken: string;
  loyaltyId?: string;
  loyaltyType?: string;
  marketingData?: MarketingData;
  offerRefId?: string;
}): Promise<LeaseApplication> => {
  return new Promise<LeaseApplication>((resolve, reject) => {
    axios
      .post<LeaseApplication>(
        '/api/lease-application/create',
        {
          type,
          partner,
          offer: {
            armada_id: offerId,
            publication: {
              pricing_id: pricingId,
              publication_id: publicationId,
            },
            offer_ref_id: offerRefId,
          },
          loyalty_card: {
            loyalty_card_id: loyaltyId,
            loyalty_card_type: loyaltyType,
          },
          vin,
          marketing_data: marketingData,
        },
        {
          timeout: REQUEST_TIMEOUT,
          method: 'post',
          headers: {
            'Accept-Language': 'de-DE',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )
      .then((response) => resolve(formatLeaseApplication(response?.data)))
      .catch((error) =>
        reject({ error: error, message: error?.response?.data }),
      );
  });
};

export const updateClient = async ({
  accessToken,
  leaseApplicationId,
  path,
  inputValue,
  version = APIVersions.BETA,
}: {
  accessToken: string;
  leaseApplicationId: string;
  path: APIPaths;
  inputValue: Record<string, unknown>;
  version?: APIVersionsType;
}): Promise<any> => {
  const SanitizedValue = await SanitizeDataValue(inputValue);
  return sandboxIsActive
    ? sandboxFunction({ path, inputValue: SanitizedValue, type: 'update' })
    : new Promise<LeaseApplication>((resolve, reject) => {
        axios
          .patch<LeaseApplication>(
            `/api/lease-application/${leaseApplicationId}/${path}`,
            { ...SanitizedValue, version },
            {
              timeout: REQUEST_TIMEOUT,
              method: 'patch',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
              },
            },
          )
          .then((response) => resolve(formatLeaseApplication(response?.data)))
          .catch((error) =>
            reject({ error: error, message: error?.response?.data }),
          );
      });
};

export const submitTrigger = (
  accessToken: string,
  leaseApplicationId: string,
  triggerCommand: string,
): Promise<LeaseApplication> => {
  return sandboxIsActive
    ? new Promise((resolve) =>
        resolve(sandboxFunction({ triggerCommand, type: 'trigger' })),
      )
    : new Promise<LeaseApplication>((resolve, reject) => {
        axios
          .post<LeaseApplication>(
            `/api/lease-application/${leaseApplicationId}`,
            { [triggerCommand]: {} },
            {
              timeout: REQUEST_TIMEOUT,
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
              },
            },
          )
          .then((response) => resolve(formatLeaseApplication(response?.data)))
          .catch((error) =>
            reject({ error: error, message: error?.response?.data }),
          );
      });
};

export const flexAdditionalCheckoutData = async ({
  id,
}: {
  id: string;
}): Promise<any> => {
  return new Promise<LeaseApplication>((resolve, reject) => {
    axios
      .get<LeaseApplication>(
        `/api/lease-application/flexAdditionalCheckoutData/${id}`,
        {
          timeout: REQUEST_TIMEOUT,
          method: 'get',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        },
      )
      .then((response) => resolve(formatLeaseApplication(response?.data)))
      .catch((error) =>
        reject({ error: error, message: error?.response?.data }),
      );
  });
};

export const getDocument = async (
  accessToken: string,
  applicationId: string,
  token: string,
  isContract?: boolean,
): Promise<any> => {
  const url = isContract
    ? `/api/lease-application/${applicationId}/document/doxis/${token}`
    : `/api/lease-application/${applicationId}/document/${token}`;
  return sandboxIsActive
    ? sandboxFunction({})
    : new Promise((resolve, reject) =>
        axios({
          method: 'GET',
          url,
          headers: {
            'content-type': 'application/octet-stream',
            Authorization: `Bearer ${accessToken}`,
          },
          responseType: 'blob',
        })
          .then((response) => resolve(response?.data))
          .catch((error) => reject(error)),
      );
};

export const uploadDocument = async (
  accessToken: string,
  applicationId: string,
  documentType: string,
  file: FormData,
  uploadProgress?: ((progressEvent: AxiosProgressEvent) => void) | undefined,
): Promise<LeaseApplication> =>
  sandboxIsActive
    ? sandboxFunction({})
    : new Promise((resolve, reject) =>
        axios({
          method: 'POST',
          url: `/api/lease-application/${applicationId}/documents`,
          data: file,
          params: {
            document_type: documentType,
          },
          headers: {
            'content-type': 'multipart/form-data',
            Authorization: `Bearer ${accessToken}`,
          },
          onUploadProgress: uploadProgress,
        })
          .then((response) => resolve(formatLeaseApplication(response?.data)))
          .catch((error) => reject(error)),
      );

export const deleteDocument = async (
  accessToken: string,
  applicationId: string,
  documentToken: string,
  documentType: string,
): Promise<LeaseApplication> =>
  sandboxIsActive
    ? sandboxFunction({})
    : new Promise((resolve, reject) =>
        axios({
          method: 'DELETE',
          url: `/api/lease-application/${applicationId}/document/${documentToken}`,
          params: {
            document_type: documentType,
          },
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        })
          .then(({ data }: { data: LeaseApplication }) =>
            resolve(formatLeaseApplication(data)),
          )
          .catch((error) => reject(error)),
      );

export const cancelLeaseApplication = (
  accessToken: string,
  leaseApplicationId: string,
): Promise<LeaseApplication> => {
  return new Promise<LeaseApplication>((resolve, reject) => {
    axios
      .delete<LeaseApplication>(
        `/api/lease-application/${leaseApplicationId}`,
        {
          timeout: REQUEST_TIMEOUT,
          headers: {
            'Accept-Language': 'de-DE',
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )
      .then((response) => resolve(response?.data))
      .catch((error) => reject(error));
  });
};

export const fetchContracts = (accessToken: string): Promise<Contracts> =>
  sandboxIsActive
    ? contracts()
    : new Promise<Contracts>((resolve, reject) => {
        axios
          .get<Contracts>('/api/lease-application/documents/signed/contracts', {
            headers: {
              'Accept-Language': 'de-DE',
              Accept: 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
          })
          .then((response) => resolve(response?.data))
          .catch((error) => reject(error));
      });

export const fetchContract = (
  accessToken: string,
  contractId: string,
): Promise<LeaseApplication> =>
  sandboxIsActive
    ? contract(contractId)
    : new Promise<LeaseApplication>((resolve, reject) => {
        axios
          .get<LeaseApplication>(`/api/lease-application/${contractId}`, {
            timeout: REQUEST_TIMEOUT,
            method: 'get',
            headers: {
              'Accept-Language': 'de-DE',
              Accept: 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
          })
          .then((response) => resolve(response?.data))
          .catch((error) => reject(error));
      });

interface SurveyResponses {
  answer_list: FeedbackAnswer[];
  browser_data: FeedbackBrowserData;
}

export const saveFeedbackSurveyResponses = (
  accessToken: string,
  responses: SurveyResponses,
): Promise<any> =>
  new Promise<any>((resolve, reject) => {
    axios
      .post<SurveyResponses>(
        '/api/lease-application/v2/surveyresponses',
        responses,
        {
          timeout: REQUEST_TIMEOUT,
          headers: {
            'Accept-Language': 'de-DE',
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )
      .then(() => resolve(undefined))
      .catch((error) => reject(error));
  });

export const updateUserExperience = (
  accessToken: string,
  leaseApplicationId: string,
  experience: UserExperience.Experience,
): Promise<UserExperience.UserExperience | undefined> =>
  new Promise<UserExperience.UserExperience | undefined>((resolve, reject) => {
    axios
      .patch<LeaseApplication>(
        `/api/lease-application/${leaseApplicationId}/updateUserExperience`,
        {
          version: 'V1_ALPHA_10',
          experience,
        },
        {
          timeout: REQUEST_TIMEOUT,
          headers: {
            'Accept-Language': 'de-DE',
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )
      .then((response) => resolve(response.data?.user_experience))
      .catch((error) => reject(error));
  });

interface UserExperienceResponses {
  experience_list: UserExperience.UserExperience[];
}

export const fetchUserExperiences = (
  auth: AuthState,
): Promise<UserExperience.UserExperience[]> =>
  new Promise<UserExperience.UserExperience[]>((resolve, reject) => {
    axios
      .get<UserExperienceResponses>(
        `/api/lease-application/userexperiences/${auth.authId}`,
        {
          timeout: REQUEST_TIMEOUT,
          headers: {
            'Accept-Language': 'de-DE',
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${auth.accessToken}`,
          },
        },
      )
      .then((response) => resolve(response?.data?.experience_list ?? []))
      .catch((error) => reject(error));
  });
